<script lang="ts">
  import { onMount } from 'svelte';
  import { manifest } from '$stores/content.js';
  import { link } from 'svelte-spa-router';
  import TableOfContents from './TableOfContents.svelte';
  import type { TableOfContentsItem } from '$types/content.js';

  let homeHtml = '';
  let toc: TableOfContentsItem[] = [];

  onMount(async () => {
    try {
      const res = await fetch('/home.json');
      if (res.ok) {
        const data = await res.json();
        homeHtml = data.html || '';
        toc = data.toc || [];
      }
    } catch {
      // home.md is optional — silently ignore if absent
    }
  });

  $: showToc = __HOME_SHOW_TOC__ && toc.length > 0;
</script>

<div class:article-container={showToc}>
  <div class="home" class:home-toc-layout={showToc}>
    <header class="hero">
      <h1>{__SITE_TITLE__}</h1>
      <p class="tagline">{__SITE_DESCRIPTION__}</p>
    </header>

    {#if homeHtml}
      <div class="home-content markdown-content">
        {@html homeHtml}
      </div>
    {/if}

    {#if __HOME_SHOW_VOLUMES__ || __HOME_SHOW_WIKI__}
      <section class="content-grid">
        {#if __HOME_SHOW_VOLUMES__}
          <div class="card">
            <h2>{__STRINGS__.home.volumesHeading}</h2>
            <p>{__STRINGS__.home.volumesDescription}</p>
            {#if $manifest && $manifest.volumes.length > 0}
              <a href="/volumes/{$manifest.volumes[0].path}" use:link class="start-link">
                {__STRINGS__.home.startReading}
              </a>
            {:else}
              <p class="empty">{__STRINGS__.home.noVolumes}</p>
            {/if}
          </div>
        {/if}

        {#if __HOME_SHOW_WIKI__}
          <div class="card">
            <h2>{__STRINGS__.home.wikiHeading}</h2>
            <p>{__STRINGS__.home.wikiDescription}</p>
            {#if $manifest && $manifest.wiki.length > 0}
              <ul class="wiki-list">
                {#each $manifest.wiki.slice(0, 5) as item}
                  <li>
                    <a href="/wiki/{item.path}" use:link>{item.title}</a>
                  </li>
                {/each}
              </ul>
            {:else}
              <p class="empty">{__STRINGS__.home.noWiki}</p>
            {/if}
          </div>
        {/if}
      </section>
    {/if}
  </div>

  {#if showToc}
    <div class="right-panel">
      <TableOfContents items={toc} />
    </div>
  {/if}
</div>

<style>
  .home {
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem;
  }

  /* When rendered inside the article-container flex row (ToC mode),
     behave like .article-page: take available space, cap at 800px. */
  .home-toc-layout {
    flex: 1;
    min-width: 0;
    max-width: 800px;
    margin: 0;
  }

  .hero {
    text-align: center;
    padding: 3rem 1rem;
    margin-bottom: 2rem;
  }

  .hero h1 {
    font-size: 3rem;
    margin: 0 0 0.5rem;
    color: var(--color-heading, #1a1a2e);
  }

  .tagline {
    font-size: 1.25rem;
    color: var(--color-text-muted, #666);
    margin: 0;
  }

  .home-content {
    margin-bottom: 2rem;
  }

  .content-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }

  .card {
    background: var(--color-surface, #fff);
    border: 1px solid var(--color-border, #e0e0e0);
    border-radius: 8px;
    padding: 1.5rem;
  }

  .card h2 {
    margin: 0 0 0.5rem;
    color: var(--color-heading, #1a1a2e);
  }

  .card p {
    color: var(--color-text, #333);
    margin: 0 0 1rem;
  }

  .start-link {
    display: inline-block;
    background: var(--color-primary, #e94560);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    text-decoration: none;
    font-weight: 500;
  }

  .start-link:hover {
    background: var(--color-primary-dark, #c73e54);
  }

  .wiki-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .wiki-list li {
    padding: 0.25rem 0;
  }

  .wiki-list a {
    color: var(--color-link, #0f3460);
    text-decoration: none;
  }

  .wiki-list a:hover {
    text-decoration: underline;
  }

  .empty {
    color: var(--color-text-muted, #999);
    font-style: italic;
  }
</style>
